import { axiosInstance } from "api/axios.instance"
import type {
	TBackendChatHistory,
	TInstructionsData,
	TSpecificQuestionPayload,
	TCustomQuestionPayload,
} from "types/aiAgent.type"

class AIAgentService {
	static BASE_INSTRUCTIONS_URL = "questions"
	static BASE_DRAWINGS_URL = "drawings"

	async getInstructions() {
		return axiosInstance.get(`${AIAgentService.BASE_INSTRUCTIONS_URL}/`)
	}

	async getQuestions(drawingId: number) {
		return axiosInstance.get(`${AIAgentService.BASE_DRAWINGS_URL}/${drawingId}/questions/`)
	}

	async askCustomQuestion(
		drawingId: number,
		subject: string,
		messages: TBackendChatHistory[],
		langsmithTraceId: string | undefined,
		question: string,
	) {
		// Initial payload
		const payload: TCustomQuestionPayload = {
			nats_subject: subject,
			langsmith_trace_id: langsmithTraceId,
			question,
		}

		// Set the messages if they exist
		if (messages.length > 0) {
			payload.messages = messages
		}

		return axiosInstance.post(`${AIAgentService.BASE_DRAWINGS_URL}/${drawingId}/questions/custom/`, payload)
	}

	async askSpecificQuestion(
		drawingId: number,
		questionId: number,
		subject: string,
		messages: TBackendChatHistory[],
		userInput?: { [key: string]: string },
		type?: string,
	) {
		// Initial payload
		const payload: TSpecificQuestionPayload = {
			nats_subject: subject,
		}

		// Set the messages if they exist
		if (messages.length > 0) {
			payload.messages = messages
		}

		// Set user input for bid requests
		if (type === "bid_request") {
			payload.user_input = userInput
		}

		return axiosInstance.post(`${AIAgentService.BASE_DRAWINGS_URL}/${drawingId}/questions/${questionId}/`, payload)
	}

	async sendFeedback(
		drawingId: number,
		langsmithTraceId: string | undefined,
		type: string,
		feedback?: string,
		drawingUrl?: string,
		manuallySubmittedByUser?: boolean,
	) {
		const payload: {
			langsmith_trace_id: string | undefined
			type: string
			feedback_text?: string
			drawing_url?: string
			manually_submitted_by_user?: boolean
		} = {
			langsmith_trace_id: langsmithTraceId,
			type,
		}

		if (feedback) {
			payload.feedback_text = feedback
		}

		if (drawingUrl) {
			payload.drawing_url = drawingUrl
		}

		if (manuallySubmittedByUser !== undefined) {
			payload.manually_submitted_by_user = manuallySubmittedByUser
		}

		return axiosInstance.post(`${AIAgentService.BASE_DRAWINGS_URL}/${drawingId}/questions/feedback/`, payload)
	}

	async addInstruction(intruction: TInstructionsData) {
		return axiosInstance.post(`${AIAgentService.BASE_INSTRUCTIONS_URL}/`, intruction)
	}

	async updateInstruction({ question, prompt, sections, questionId }: TInstructionsData) {
		return axiosInstance.patch(`${AIAgentService.BASE_INSTRUCTIONS_URL}/${questionId}/`, {
			question,
			prompt,
			sections,
		})
	}

	async deleteInstruction(questionId: number) {
		return axiosInstance.delete(`${AIAgentService.BASE_INSTRUCTIONS_URL}/${questionId}/`)
	}

	async updateInstructionStatus(questionId: number, state: string) {
		return axiosInstance.patch(`${AIAgentService.BASE_INSTRUCTIONS_URL}/${questionId}/state/`, { state })
	}

	async addBidRequest(
		drawingId: number,
		langsmithTraceId: string | undefined,
		llmSelections: Record<string, string>,
		userConfirmation: Record<string, string>,
		seriesName: string,
	) {
		return axiosInstance.post(`${AIAgentService.BASE_DRAWINGS_URL}/${drawingId}/bid-request/submit/`, {
			langsmith_trace_id: langsmithTraceId,
			llm_selections: llmSelections,
			user_confirmations: userConfirmation,
			series_name: seriesName,
		})
	}
}
export default new AIAgentService()
